import { useAuthStore } from '@/stores/authStore';

export class ApiError extends Error {
  status: number;
  code?: string;
  requestId?: string;

  constructor(status: number, message: string, code?: string, requestId?: string) {
    super(message);
    this.status = status;
    this.code = code;
    this.requestId = requestId;
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  query?: Record<string, string | number | boolean | null | undefined>;
}

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${API_BASE}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === null || v === undefined || v === '') continue;
    params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

export async function apiFetch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { body, query, headers, ...rest } = opts;
  const token = useAuthStore.getState().token;

  const hasBody = body !== undefined;
  const res = await fetch(buildUrl(path, query), {
    ...rest,
    headers: {
      // Only declare a content-type when we actually send a body — Fastify's
      // strict JSON parser 400s on a Content-Type: application/json with an
      // empty Content-Length (which the browser sets for DELETE/GET).
      ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
    body: hasBody ? JSON.stringify(body) : undefined,
    credentials: 'include',
  });

  if (res.status === 401) {
    useAuthStore.getState().clear();
    throw new ApiError(401, 'Session expirée');
  }

  const text = await res.text();
  const json = text ? (safeJson(text) as Record<string, unknown> | null) : null;

  if (!res.ok) {
    const err = (json?.error ?? {}) as { message?: string; code?: string; request_id?: string };
    throw new ApiError(res.status, err.message ?? res.statusText, err.code, err.request_id);
  }

  return (json ?? {}) as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query']) => apiFetch<T>(path, { method: 'GET', query }),
  post: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string) => apiFetch<T>(path, { method: 'DELETE' }),
};
